/**
 * AppShell v2 — YouTube-Studio-inspired persistent sidebar + topbar.
 * Replaces the old dark-sidebar AppShell. Uses react-router for navigation.
 */
import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Icon, Btn } from "./primitives.tsx";
import { useAuthStore } from "@/stores/authStore.ts";
import { useTenant } from "@/stores/tenantStore.tsx";
import { creditsApi } from "@/api/credits.ts";

const NAV_ITEMS = [
  { to: "/dashboard",  icon: "home",     label: "Dashboard",  mobile: true },
  { to: "/videos",     icon: "video",    label: "Videos",     mobile: true },
  // the business strategy each engine video is built from (required on the engine; it was unreachable from this menu)
  { to: "/strategy",   icon: "layers",   label: "Strategy",   mobile: false },
  { to: "/channels",   icon: "folder",   label: "Channels",   mobile: false },
  { to: "/assets",     icon: "image",    label: "Assets",     mobile: false },
  { to: "/analytics",  icon: "stats",    label: "Analytics",  mobile: false },
  { to: "/referrals",  icon: "share",    label: "Referrals",  mobile: false },
];

const MOBILE_NAV = [
  { to: "/dashboard",  icon: "home",  label: "Dashboard" },
  { to: "/videos",     icon: "video", label: "Videos" },
  { to: "/videos/new", icon: "plus",  label: "New", accent: true },
  { to: "/analytics",  icon: "stats", label: "Analytics" },
];

function TFLogo() {
  const { siteName } = useTenant();
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, letterSpacing: "-0.01em", fontSize: 15, color: "var(--tf-fg)" }}>
      <div style={{ width: 22, height: 22, borderRadius: 6, background: "var(--tf-accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name="sparkle" size={12} color="#fff" />
      </div>
      {siteName}
    </div>
  );
}

export function AppShellV2() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="tf-shell">
      {/* SIDEBAR (desktop) */}
      <aside className="tf-sidebar">
        <div style={{ padding: "18px 18px 12px" }}>
          <TFLogo />
        </div>
        <div style={{ padding: "8px 12px" }}>
          <Btn fullWidth icon="plus" onClick={() => navigate("/videos/new")}>New video</Btn>
        </div>
        <nav style={{ padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2 }}>
          {NAV_ITEMS.map(r => (
            <NavLink
              key={r.to}
              to={r.to}
              end={r.to === "/dashboard"}
              className={({ isActive }) => "tf-nav-item" + (isActive || (r.to === "/videos" && location.pathname.startsWith("/videos/")) ? " active" : "")}
            >
              <Icon name={r.icon} size={18} />
              <span>{r.label}</span>
            </NavLink>
          ))}
        </nav>
        <div style={{ marginTop: "auto", padding: 12, borderTop: "1px solid var(--tf-rule)", display: "flex", flexDirection: "column", gap: 2 }}>
          <NavLink to="/billing" className={({ isActive }) => "tf-nav-item" + (isActive ? " active" : "")}>
            <Icon name="creditCard" size={18} /><span>Billing</span>
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => "tf-nav-item" + (isActive ? " active" : "")}>
            <Icon name="settings" size={18} /><span>Settings</span>
          </NavLink>
          <a
            href="/help"
            target="_blank"
            rel="noopener noreferrer"
            className="tf-nav-item"
          >
            <Icon name="help" size={18} /><span>Help</span>
          </a>
          <NavLink to="/admin" className={({ isActive }) => "tf-nav-item" + (isActive ? " active" : "")}>
            <Icon name="shield" size={18} /><span>Admin</span>
          </NavLink>
        </div>
      </aside>

      {/* MAIN COL */}
      <div className="tf-main">
        <TopBar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <main style={{ flex: 1, overflow: "auto", paddingBottom: 80 }}>
          <Outlet />
        </main>
      </div>

      {/* MOBILE BOTTOM NAV */}
      <div className="tf-mobile-nav">
        {MOBILE_NAV.map(r => (
          <NavLink
            key={r.to}
            to={r.to}
            style={({ isActive }) => ({
              flex: 1, background: "transparent", border: 0, padding: "10px 4px",
              display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 4,
              color: isActive ? "var(--tf-accent)" : "var(--tf-fg-muted)",
              cursor: "pointer", fontFamily: "inherit", textDecoration: "none",
            })}
          >
            {r.accent
              ? <span style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--tf-accent)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name={r.icon} size={16} /></span>
              : <Icon name={r.icon} size={20} />
            }
            <span style={{ fontSize: 10, fontWeight: 500 }}>{r.label}</span>
          </NavLink>
        ))}
      </div>

      {/* MOBILE NAV OVERLAY */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 60 }}>
          <div onClick={e => e.stopPropagation()} style={{
            position: "absolute", top: 0, left: 0, bottom: 0, width: 260,
            background: "var(--tf-bg)", borderRight: "1px solid var(--tf-rule)",
            display: "flex", flexDirection: "column", gap: 4, padding: 16,
          }}>
            <div style={{ marginBottom: 14 }}><TFLogo /></div>
            {NAV_ITEMS.map(r => (
              <NavLink
                key={r.to}
                to={r.to}
                onClick={() => setMobileOpen(false)}
                className="tf-nav-item"
              >
                <Icon name={r.icon} size={18} /><span>{r.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TopBar({ setMobileOpen }: { mobileOpen?: boolean; setMobileOpen: (v: boolean) => void }) {
  const [showCmd, setShowCmd] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  const { data: balanceData } = useQuery({
    queryKey: ["credits", "balance"],
    queryFn: () => creditsApi.balance(),
    refetchInterval: 60_000,
    enabled: !!user,
  });
  const videoCredits = Math.floor((balanceData?.balance ?? 0) / 100);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setShowCmd(s => !s); }
      if (e.key === "Escape") setShowCmd(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  const pageLabel = getPageLabel(location.pathname);

  return (
    <div className="tf-topbar">
      <button onClick={() => setMobileOpen(true)} aria-label="Open menu" className="tf-mobile-only" style={{
        background: "transparent", border: 0, padding: 6, cursor: "pointer", color: "var(--tf-fg)",
      }}><Icon name="menu" size={22} /></button>

      <div className="tf-topbar-breadcrumb tf-mobile-hide">
        <span className="tf-muted" style={{ fontSize: 13 }}>TubeForge</span>
        <Icon name="chevronRight" size={12} color="var(--tf-fg-faint)" />
        <span style={{ fontSize: 13, fontWeight: 500 }}>{pageLabel}</span>
      </div>

      <div style={{ flex: 1 }} />

      <div className="tf-topbar-search tf-mobile-hide" onClick={() => setShowCmd(true)}>
        <Icon name="search" size={14} color="var(--tf-fg-muted)" />
        <span style={{ flex: 1, color: "var(--tf-fg-muted)", fontSize: 13 }}>Search videos, channels…</span>
        <span className="tf-mono" style={{ fontSize: 11, padding: "2px 6px", background: "var(--tf-surface)", border: "1px solid var(--tf-rule)", borderRadius: 4, color: "var(--tf-fg-muted)" }}>⌘K</span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {/* Credits */}
        <span className="tf-mobile-hide" style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "4px 10px", fontSize: 11, fontWeight: 500,
          background: "var(--tf-surface)", border: "1px solid var(--tf-rule)",
          borderRadius: 6, color: "var(--tf-fg-muted)",
          fontFamily: "var(--tf-mono)",
        }}>
          {videoCredits} credits
        </span>

        <button className="tf-icon-btn" aria-label="Notifications"><Icon name="bell" size={18} /></button>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 6, padding: "4px 4px 4px 10px", borderLeft: "1px solid var(--tf-rule)" }}>
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: "linear-gradient(135deg, var(--tf-accent), #ec4899)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 600,
          }}>
            {user?.email?.[0]?.toUpperCase() ?? "?"}
          </div>
        </div>
      </div>

      {showCmd && (
        <CommandPalette
          onClose={() => setShowCmd(false)}
          onNavigate={(p) => { navigate(p); setShowCmd(false); }}
        />
      )}
    </div>
  );
}

function getPageLabel(pathname: string): string {
  if (pathname === "/dashboard") return "Dashboard";
  if (pathname === "/videos/new") return "New video";
  if (pathname.includes("/script")) return "Script review";
  if (pathname.includes("/storyboard")) return "Storyboard review";
  if (pathname.includes("/render")) return "Rendering";
  if (pathname.startsWith("/videos/") && !pathname.includes("/")) return "Video";
  if (pathname === "/videos") return "Videos";
  if (pathname === "/strategy") return "Strategy";
  if (pathname === "/analytics") return "Analytics";
  if (pathname === "/channels") return "Channels";
  if (pathname.startsWith("/channels/")) return "Channel";
  if (pathname === "/referrals") return "Referrals";
  if (pathname === "/settings") return "Settings";
  if (pathname === "/billing") return "Billing";
  if (pathname.startsWith("/admin")) return "Admin";
  return "";
}

function CommandPalette({ onClose, onNavigate }: { onClose: () => void; onNavigate: (p: string) => void }) {
  const [q, setQ] = useState("");
  const items = [
    { kind: "Navigate", label: "Dashboard",     path: "/dashboard",  icon: "home" },
    { kind: "Navigate", label: "Videos",         path: "/videos",     icon: "video" },
    { kind: "Navigate", label: "New video",      path: "/videos/new", icon: "plus" },
    { kind: "Navigate", label: "Channels",       path: "/channels",   icon: "folder" },
    { kind: "Navigate", label: "Analytics",      path: "/analytics",  icon: "stats" },
    { kind: "Navigate", label: "Settings",       path: "/settings",   icon: "settings" },
    { kind: "Navigate", label: "Billing",        path: "/billing",    icon: "creditCard" },
  ];
  const filtered = items.filter(i => i.label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 100, zIndex: 80 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 92vw)", background: "var(--tf-bg)", border: "1px solid var(--tf-rule)", borderRadius: 12, boxShadow: "var(--tf-shadow-md)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "1px solid var(--tf-rule)" }}>
          <Icon name="search" size={16} color="var(--tf-fg-muted)" />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search or jump to…"
            style={{ flex: 1, border: 0, outline: 0, fontFamily: "inherit", fontSize: 14, color: "var(--tf-fg)", background: "transparent" }}
          />
          <span className="tf-mono tf-muted" style={{ fontSize: 11 }}>esc</span>
        </div>
        <div style={{ maxHeight: 360, overflow: "auto", padding: 6 }}>
          {filtered.length === 0 && <div style={{ padding: 18, fontSize: 13, color: "var(--tf-fg-muted)" }}>No results</div>}
          {filtered.map((it, i) => (
            <button
              key={i}
              onClick={() => onNavigate(it.path)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "9px 12px",
                background: "transparent", border: 0, borderRadius: 8, cursor: "pointer",
                fontFamily: "inherit", textAlign: "left", color: "var(--tf-fg)",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "var(--tf-surface)")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              <Icon name={it.icon} size={16} color="var(--tf-fg-muted)" />
              <span style={{ flex: 1, fontSize: 13 }}>{it.label}</span>
              <span className="tf-mono tf-muted" style={{ fontSize: 10 }}>{it.kind}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
